import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  customizeFormatAction,
  customizeMeasureColorAction,
} from '../../../../redux/slices/analyses';
import styled, { css } from 'styled-components';
import { CHART_TYPES } from '../../constants/chartTypes';
import AliasEditorWrapper from '../../../AliasEditor/AliasEditorWrapper';
import { IDisplayedMeasure } from '../../interfaces';
import {
  DateKindKey,
  FunnelModeKey,
  IMeasureDisplayFormatFieldsFragment,
  IMeasureFieldsFragment,
  PermissionKey,
  QueryType,
  SimpleCohortTypeKey,
  TimeToConvertOperatorKey,
  TimeUnit,
} from '../../../../utils/apollo/graphqlSchema';
import { IContextualMenuItem } from '@fluentui/react';
import FormatMenu from './FormatMenu';
import prepareCachedCohort from '../../../../utils/createCohort/prepareCachedCohort';
import { saveCachedCohortAndOpenModalThunk } from '../../../Builder/Cohorts/CohortRow/utils';
import ContextualMenuSubItemWrapper from '../../../Dropdown/ContextualMenu/ContextualMenuSubItemWrapper';
import { isTransientAnalysisId } from '../../../../redux/utils/transientAnalysis';
import { SUBJECT_EVENT_KEY } from '../../constants/utils';
import { assertCondition } from '../../../../utils/types';
import ColorMenu from '../ColorMenu';
import { getMeasureColorFromColorConfig } from '../../utils/analysisColorConfig';
import {
  ICohortDurationStart,
  ILookForwardWindow,
} from '../../../../redux/slices/oldBuilder';
import {
  parseCohortDurationStartDate,
  parseFunnelLookForwardWindow,
} from '../../../../redux/slices/oldBuilder/utils';
import { useCurrentUrl } from '../../../../utils/routing';
import { useSaveMeasure } from './utils/useSaveMeasure';
import usePermission from '../../../../utils/hooks/permissionHooks/usePermission';
import ContextualMenuOption from '../../../Dropdown/ContextualMenu/ContextualMenuOption';
import { saveMeasureTooltipOption } from '../../../KubitTooltip/globalTooltips';
import SaveMeasureModalController from './SaveMeasureModal';
import isEqual from 'lodash/isEqual';
import { useAppSelector } from '../../../../redux/hooks';
import { selectOpenedModalType } from '../../../../redux/selectors/modal';
import { ModalSliceType } from '../../../../redux/slices/modal';
import { findMeasureIdByMeasureKeyInBuilderInfo } from '../../../Builder/utils/builderUtils';
import useFunnelSteps from './useFunnelSteps';
import RenamedIndicator from '../../../RenamedIndicator';
import { getCohortPagePath } from '../../../../utils';
import { theme } from '../../../../styles/theme';
import { useChartContext } from '../../ChartContext';
import { colorMenuStyles } from '../ColorMenu/ColorMenu';

interface IProps {
  analysisId: string;
}

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const MeasureButton = styled.button<{
  $isActive: boolean;
  $isNotAllowed: boolean;
}>`
  display: inline-flex;
  align-items: center;
  column-gap: 2px;
  min-width: 48px;
  width: fit-content;
  white-space: nowrap;
  padding: 3px 6px;
  height: 24px;
  border: 1px solid transparent;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $isActive }) =>
    $isActive ? theme.colors.secondary800 : theme.colors.secondary400};
  background: none;
  border-radius: 10px;
  font: inherit;
  font-weight: 600;
  line-height: 16px;
  cursor: ${({ $isNotAllowed }) => ($isNotAllowed ? 'not-allowed' : 'pointer')};
  // not allowed is not interactable, but not disabled.
  ${({ $isNotAllowed }) =>
    !$isNotAllowed &&
    css`
      &:not(:disabled):hover {
        border-color: ${theme.colors.mainBlue};
        color: ${theme.colors.mainBlue};
      }
    `}
`;

/**
 * Allow Format option on these charts only
 */
const ALLOW_FORMAT_CHARTS = [
  CHART_TYPES.LINE_CHART,
  CHART_TYPES.BAR_CHART,
  CHART_TYPES.PERCENT_STACKED_AREA_CHART,
  CHART_TYPES.STACKED_COLUMN_CHART,
  CHART_TYPES.GEO_MAP,
  CHART_TYPES.METRIC_CHART,
  CHART_TYPES.PIE_CHART,
];

export default ({ analysisId }: IProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    displayedMeasures,
    colorConfig,
    chartData,
    metadata: { queryType, startDate, endDate, query, isEmbedded },
    settings: { chartSettings },
  } = useChartContext();
  const { chartType } = chartSettings;

  assertCondition(
    query.__typename === 'FunnelQuery' ||
      query.__typename === 'ExpressionQuery' ||
      query.__typename === 'RetentionQuery' ||
      query.__typename === 'PathQuery',
  );
  const schemaId = query.schema.id;
  const { updatedDisplayedMeasures } = useFunnelSteps({
    analysisId,
    queryType,
    displayedMeasures,
    builderInfo: query,
    chartData,
    colorConfig,
  });
  const { isReportInUrl, isDebugInUrl } = useCurrentUrl();

  const { checkIsPermitted } = usePermission();
  const isPermittedToSaveMeasure = checkIsPermitted({
    requiredPermissionKeys: [
      PermissionKey.MeasureCreate,
      PermissionKey.MeasureEdit,
    ],
    validateKind: 'OR',
    schemaId,
  });

  const isPermittedToCreateAnalysis = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AnalysisCreate],
    schemaId,
  });

  // only allow control measure on non-geo map charts
  const isAllowedToControlMeasure = chartType !== CHART_TYPES.GEO_MAP;

  // KBT-186: hide color select on measure in diff cases, more details in
  // https://kubitai.atlassian.net/wiki/spaces/PROD/pages/2175041537/Color+Enhancement+Specification+Document
  const hasGroupbyOrSegment =
    (query.breakdown?.groupbys?.length ?? 0) > 0 ||
    (query.breakdown?.cohortRefs?.length ?? 0) > 0;

  const hideColorSelect =
    hasGroupbyOrSegment &&
    (query.__typename === 'ExpressionQuery' ||
      query.__typename === 'FunnelQuery');

  const isTransientAnalysis = isTransientAnalysisId(analysisId);

  const allowCreateCohort = useMemo(() => {
    // disable Cohort creation if the schema is disabled
    if (query.schema.disabled) {
      return false;
    }
    // Only analysis with "Event" date is supported in cohort
    const dateKindCheck =
      'dateKind' in query && query.dateKind?.key === DateKindKey.Event;

    if (query.__typename === 'FunnelQuery') {
      // funnels with Event subject are not supported
      return dateKindCheck && query?.subject?.key !== SUBJECT_EVENT_KEY;
    }

    return dateKindCheck;
  }, [query]);

  const updateMeasureFormat = useCallback(
    (measureKey: string, newFormat: IMeasureDisplayFormatFieldsFragment) => {
      dispatch(
        customizeFormatAction({
          analysisId,
          measureKey,
          customMeasureFormat: newFormat,
        }),
      );
    },
    [analysisId, dispatch],
  );

  const handleChangeFormat = useCallback(
    (
      measure: IDisplayedMeasure,
      dismissMenu: (ev?: any, dismissAll?: boolean) => void,
    ) =>
      (values: IMeasureDisplayFormatFieldsFragment) => {
        updateMeasureFormat(measure.key, values);

        dismissMenu(undefined, true);
      },
    [updateMeasureFormat],
  );

  const handleCreateCohort = useCallback(
    (measure: IDisplayedMeasure, isExecuteAfterRedirect: boolean) => {
      let stepIndex;
      let measureId;
      const type =
        queryType === QueryType.Funnel
          ? SimpleCohortTypeKey.Funnel
          : SimpleCohortTypeKey.Expression;
      const isTimeToConvertMode =
        query.__typename === 'FunnelQuery' &&
        query.mode?.key === FunnelModeKey.TimeToConvert;
      if (type === SimpleCohortTypeKey.Funnel) {
        const measureStepIndex = measure.key.replace(/[^\d.-]+/g, '');

        if (measureStepIndex?.length) {
          stepIndex = +measureStepIndex - 1;
        }
      }

      if (type === SimpleCohortTypeKey.Expression) {
        assertCondition(query.__typename === 'ExpressionQuery');
        measureId = findMeasureIdByMeasureKeyInBuilderInfo(query, measure.key);
      }

      let cohortDurationStart: ICohortDurationStart | undefined;
      let lookForwardWindow: ILookForwardWindow | undefined;
      if (
        query.__typename === 'FunnelQuery' ||
        query.__typename === 'RetentionQuery'
      ) {
        cohortDurationStart = parseCohortDurationStartDate(query, startDate);
      }

      if (query.__typename === 'FunnelQuery') {
        lookForwardWindow = parseFunnelLookForwardWindow(query, null);
      }

      const cachedCohortData = prepareCachedCohort({
        analysisId,
        measureId,
        referToNewMeasure: !!measureId,
        type,
        startDate,
        endDate,
        builderInfo: query,
        index: stepIndex,
        isExecuteAfterRedirect,
        cohortDurationStart,
        lookForwardWindow,
        timeToConvertInput: isTimeToConvertMode
          ? {
              operator: TimeToConvertOperatorKey.GreaterThan,
              timeUnit: TimeUnit.Day,
              values: [0],
            }
          : null,
      });

      dispatch(
        saveCachedCohortAndOpenModalThunk(
          cachedCohortData,
          isExecuteAfterRedirect,
          analysisId,
        ),
      );

      if (isExecuteAfterRedirect) {
        navigate(getCohortPagePath());
      }
    },
    [analysisId, query, dispatch, endDate, navigate, queryType, startDate],
  );

  const handleSaveMeasure = useSaveMeasure(query);

  const handleSubmitNewMeasure = useCallback(
    (measure: IDisplayedMeasure) => (newMeasure: IMeasureFieldsFragment) => {
      const oldFormat = measure?.format;
      const newFormat = newMeasure?.format;

      // Format will be saved as a part of named measure
      // User can change measure format in Save Measure modal
      // We will apply any format change to a chart
      if (newFormat && !isEqual(oldFormat, newFormat)) {
        updateMeasureFormat(measure.key, newFormat);
      }
    },
    [updateMeasureFormat],
  );

  const saveOptionAvailable =
    (isDebugInUrl || isReportInUrl) && queryType === QueryType.Expression;

  const isSaveMeasureModalOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.SaveMeasure, ''),
  );

  const handleChangeMeasureColor = useCallback(
    (measure: IDisplayedMeasure) => (customColor: string) => {
      dispatch(
        customizeMeasureColorAction({
          analysisId,
          measureKey: measure.key,
          customColor,
        }),
      );
    },
    [analysisId, dispatch],
  );

  const getExtraMenuItems = useCallback(
    (measure: IDisplayedMeasure) => {
      const menuItems: IContextualMenuItem[] = [];
      const currentColor = getMeasureColorFromColorConfig(
        colorConfig,
        measure.key,
      );

      if (chartType !== CHART_TYPES.GEO_MAP && !hideColorSelect) {
        menuItems.push({
          key: 'custom-measure-color',
          onRenderContent: () => (
            <ContextualMenuOption label={'Select Color'} hasSubMenus={true} />
          ),
          subMenuProps: {
            styles: colorMenuStyles,
            items: [
              {
                key: 'custom-measure-color-menu',
                onRender: () => (
                  <ContextualMenuSubItemWrapper>
                    <ColorMenu
                      currentColor={currentColor}
                      onSubmit={handleChangeMeasureColor(measure)}
                    />
                  </ContextualMenuSubItemWrapper>
                ),
              },
            ],
          },
        });
      }

      // if it's occurrence chart, we don't allow user modify the calculation
      // because it doesn't make sense at all
      const isOccurrenceChart =
        'subjectBucketDef' in query && !!query.subjectBucketDef;

      const hideCalculation =
        (query.__typename === 'ExpressionQuery' &&
          // if formula is null, we allow calculation on all measures,
          // since it doesn't impact the calculation result of result measure.
          // therefore hide if formula is not null and it's not result measure
          query.measure.formula &&
          !measure.isResultKey) ||
        // for histogram chart, we don't allow calculation on any measure for keeping the result correct
        isOccurrenceChart;

      // only allow format in chart types from Expression query
      if (ALLOW_FORMAT_CHARTS.includes(chartType)) {
        menuItems.push({
          key: 'Format',
          onRenderContent: () => (
            <ContextualMenuOption label={'Format'} hasSubMenus={true} />
          ),
          subMenuProps: {
            items: [
              {
                key: 'format-menu',
                onRender: (_: unknown, dismissMenu) => (
                  <ContextualMenuSubItemWrapper>
                    <FormatMenu
                      measureKey={measure.key}
                      onSubmit={handleChangeFormat(measure, dismissMenu)}
                      hideCalculation={hideCalculation}
                    />
                  </ContextualMenuSubItemWrapper>
                ),
              },
            ],
          },
        });
      }

      if (saveOptionAvailable) {
        menuItems.push({
          key: 'save-measure',
          onRenderContent: () => (
            <ContextualMenuOption
              label={'Save Measure'}
              onClick={() =>
                handleSaveMeasure({
                  displayedMeasure: measure,
                  onSubmit: handleSubmitNewMeasure(measure),
                  hideCalculation,
                })
              }
              isUnPermitted={!isPermittedToSaveMeasure}
              tooltipOption={saveMeasureTooltipOption}
              actionLinkAsSpan={true}
            />
          ),
        });
      }

      if (
        allowCreateCohort &&
        (queryType === QueryType.Funnel ||
          queryType === QueryType.Expression) &&
        !isEmbedded
      ) {
        menuItems.push(
          {
            key: 'create-cohort',
            onRenderContent: () => (
              <ContextualMenuOption
                label={'Create Cohort'}
                onClick={() => handleCreateCohort(measure, false)}
                isUnPermitted={!isPermittedToCreateAnalysis}
              />
            ),
          },
          {
            key: 'inspect-users',
            onRenderContent: () => (
              <ContextualMenuOption
                label={'Inspect Users'}
                onClick={() => handleCreateCohort(measure, true)}
                isUnPermitted={!isPermittedToCreateAnalysis}
              />
            ),
          },
        );
      }

      return menuItems;
    },
    [
      colorConfig,
      chartType,
      hideColorSelect,
      query,
      saveOptionAvailable,
      allowCreateCohort,
      queryType,
      handleChangeMeasureColor,
      handleChangeFormat,
      isPermittedToSaveMeasure,
      handleSaveMeasure,
      handleSubmitNewMeasure,
      isPermittedToCreateAnalysis,
      handleCreateCohort,
      isEmbedded,
    ],
  );

  if (!updatedDisplayedMeasures.length) {
    return null;
  }

  return (
    <>
      <Container>
        {updatedDisplayedMeasures.map((measure) => {
          const { isVisible, nameElement, aliases, onClickHandler } = measure;
          const measureChartOptions = getExtraMenuItems(measure);

          // Special case: skip actions for transient analysis
          if (isTransientAnalysis) {
            return (
              <MeasureButton
                key={measure.key}
                type="button"
                onClick={onClickHandler}
                $isNotAllowed={!isAllowedToControlMeasure}
                $isActive={isVisible}
              >
                {nameElement}
                {measure.hasUserAlias && <RenamedIndicator />}
              </MeasureButton>
            );
          }

          return (
            <AliasEditorWrapper
              disabled={!measure.isVisible}
              aliases={aliases}
              key={measure.key}
              inline={true}
              extraMenuItems={measureChartOptions}
            >
              <MeasureButton
                type="button"
                onClick={onClickHandler}
                disabled={!isAllowedToControlMeasure}
                $isNotAllowed={!isAllowedToControlMeasure}
                $isActive={isVisible}
              >
                {nameElement}
                {measure.hasUserAlias && <RenamedIndicator />}
              </MeasureButton>
            </AliasEditorWrapper>
          );
        })}
      </Container>
      {saveOptionAvailable && isSaveMeasureModalOpen && (
        <SaveMeasureModalController schemaId={schemaId} />
      )}
    </>
  );
};
